import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Field, Form, Formik, FormikProvider, useFormik } from 'formik';

// MUI Components
import ArrowBack from '@mui/icons-material/ArrowBack';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '../../../components/Button/index.js';

import ProgressBar from '../../../components/ProgressBar/index.js';
import useUser from '../../../Hooks/useUser.js';
import Loading from '../../../components/Loading/index.js';
import WorkbookItem from '../../../components/WorkbookItem/index.js';
import { postDataAPI, putDataAPI } from '../../../Helpers/FetchAPI';
import Certification from '../../../components/molecules/clientWorbook/certification.js';
import RenderString from '../../../components/RenderString/index.js';
import useNotebooks from '../../../Hooks/useNotebooks.js';
import { WorkbookNav } from '../../../components/🦠organisms/WorkbookNav';
import { API_URL } from '../../../constant';
import workbookImageTemp from '../../../images/pd-resource-square.png';
import logo from '../../../images/logo/pure_desire_logo_black.svg';
import { ToolDrawer } from '../../../components/ToolDrawer/ToolDrawer';
import { PreviousNoteGroup } from '../../../components/❉molecules/PreviousNoteGroup';
import { XCircleIcon } from '@heroicons/react/24/solid';
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/outline';
import { Swipeable } from '../../../components/⚛️atoms/Swipeable';
import toast from 'react-hot-toast';
import { useDebounce, useScroll, useScrolling, useWindowSize } from 'react-use';
import { handleExpiredCreditError, getCreditErrorMessage } from '../../../utils/creditErrorHandler';
import { Walktour } from 'walktour';
import {useTranslation} from "react-i18next";
import { isCreditExpired, isCreditExpiringSoon, getDaysUntilExpiration, formatExpirationDate } from '../../../utils/creditExpiration';
import { CreditExpirationBadge } from '../../../components/⚛️atoms/CreditExpirationBadge';

const Book = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { currentUser } = useUser();
  const notebookDrawer = useRef(null);

  const lessonContentRef = useRef(null);

  const { width } = useWindowSize();

  const [certLoading, setCertLoading] = useState(false);
  const [certification, setCertification] = useState('');
  const [selectedSection, setSelectedSection] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [showSection, setShowSection] = useState(null);
  const [score, setScore] = useState(false);

  const [errorQuestions, setErrorQuestions] = useState([]);
  const [saving, setActualSaving] = useState('');
  const [expandNav, setExpandNav] = useState(false);
  const [showToolDrawer, setShowToolDrawer] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [initContent, setInitContent] = useState('');
  const [lastNoteSubmit, setLastNoteSubmit] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [journalTitle, setJournalTitle] = useState('');

  const { x, y: contentScrollY } = useScroll(lessonContentRef);
  const scrolling = useScrolling(lessonContentRef);

  const [showNextPrevHint, setShowNextPrevHint] = useState(false);

  const { addNewEntry, updateEntry, deleteEntry, addNewJournal } = useNotebooks();
  const { t } = useTranslation();
  useEffect(() => {
    const current = lessonContentRef.current;
    if (current) {
      setShowNextPrevHint(current.scrollHeight - current.offsetHeight === contentScrollY);
    }
  }, [contentScrollY, scrolling]);

  const oneToastId = 'To Rule Them All';

  const setSaving = (data) => {
    if (data.message?.startsWith('Err')) {
      toast.error(data.message, {
        id: oneToastId,
        style: {
          minWidth: '150px'
        }
      });
    } else if (data.message?.startsWith('Saving')) {
      toast.loading(data.message, {
        id: oneToastId,
        style: {
          minWidth: '150px'
        }
      });
    } else if (data.message?.startsWith('Saved')) {
      toast.success(data.message, {
        id: oneToastId,
        style: {
          minWidth: '150px'
        }
      });
    }
    setActualSaving(data);
  };

  useEffect(() => {
    if (submitLoading) {
      toast.loading('Saving...', {
        id: oneToastId,
        position: width > 1024 ? 'bottom-right' : '',
        style: {
          minWidth: '150px'
        }
      });
    } else {
      toast.success('Saved', {
        id: oneToastId,
        position: width > 1024 ? 'bottom-right' : '',
        style: {
          minWidth: '150px'
        }
      });
    }
  }, [submitLoading]);

  const { isLoading, error, data } = useQuery({
    queryKey: ['workbook', id, currentUser],
    queryFn: () => {
      return postDataAPI('/api/user/workbook', {
        credit_id: id,
        user_id: currentUser?._id
      }).then((res) => res.data);
    },
    enabled: !!id && !!currentUser
  });

  useEffect(() => {
    if (error) {
      if (!handleExpiredCreditError(error)) {
        toast.error(getCreditErrorMessage(error));
      }
    }
  }, [error]);

  const handleSelectedSectionChange = (selectedSection) => {
    if (!selectedSection && progress.value === progress.maximumValue) {
      setSelectedSection('certificate');
      setShowSection('certificate');
      setActiveId('certificate');
      return;
    }
    setSelectedSection(selectedSection);
    setActiveId(selectedSection?._id);
  };

  const scrollWorkbookContentToTop = () => {
    const workbookWindow = document.getElementById('workbookContent');

    if (workbookWindow && selectedSection) {
      workbookWindow.scrollTop = 0;
    }
  };

  const handleSubmitEntry = useCallback(
    (edit) => {
      if (initContent === '') {
        return;
      }
      setSubmitLoading(true);
      if (edit) {
        selectedEntry.content = initContent;
        selectedEntry.title = noteTitle;
        updateEntry(selectedEntry, () => {
          queryClient.invalidateQueries({ queryKey: ['notebook'] });
          setTimeout(() => setSubmitLoading(false), 300);
          setLastNoteSubmit(Date.now());
        });
      } else {
        addNewEntry(
          {
            workbookId: data?._id,
            userId: currentUser?._id,
            entry: initContent,
            creditId: id,
            title: noteTitle
          },
          (data) => {
            setTimeout(() => setSubmitLoading(false), 500);
            queryClient.invalidateQueries({ queryKey: ['notebook'] });
            setSelectedEntry(data.data.data);
            setLastNoteSubmit(Date.now());
          }
        );
      }
    },
    [initContent, noteTitle, addNewEntry, id, currentUser, selectedEntry]
  );

  useDebounce(
    () => {
      handleSubmitEntry(selectedEntry);
    },
    250,
    [initContent, noteTitle]
  );

  const handleClearNote = () => {
    setInitContent('');
    setNoteTitle('');
    setSelectedEntry(null);
  };
  useCallback(() => {
    setSubmitLoading(true);
    addNewJournal(
      {
        userId: currentUser?._id,
        entry: initContent,
        title: journalTitle
      },
      () => {
        setSubmitLoading(false);
        setInitContent('');
        setJournalTitle('');
        queryClient.invalidateQueries({ queryKey: ['notebook'] });
      }
    );
  }, [initContent, addNewEntry, id, currentUser, selectedEntry, journalTitle]);

  useEffect(() => {
    if (selectedEntry) {
      setInitContent(selectedEntry.content);
      setNoteTitle(selectedEntry.title);
    }
  }, [selectedEntry]);
  useCallback(
    (entry) => {
      setSubmitLoading(true);
      deleteEntry(entry, () => {
        setInitContent('');
        setSelectedEntry(null);
        setSubmitLoading(false);
        queryClient.invalidateQueries({ queryKey: ['notebook'] });
      });
      setDeleteSnack(true);
    },
    [deleteEntry]
  );
  const userWorkbookItemMutation = useMutation({
    mutationFn: async (params) => {
      return await postDataAPI('/userWorkbookItems/passItem/', params);
    },
    onSuccess: () => {
      setSaving({
        message: 'Saved',
        disabled: false
      });
      setScore(false);
      queryClient.invalidateQueries({ queryKey: ['workbook'] });
    },
    onError: (err) => {
      alert(err.message);
    }
  });

  const userworkbookItemUpdateMutation = useMutation({
    mutationFn: async (params) => {
      return await putDataAPI(`/userWorkbookItems/item/${params.id}`, params.param);
    },
    onSuccess: () => {
      setSaving({
        message: 'Saved',
        disabled: false
      });
      queryClient.invalidateQueries({ queryKey: ['workbook'] });
      setScore(false);
    },
    onError: (err) => {
      if (handleExpiredCreditError(err)) {
          setSaving({
            message: 'Credit expired - unable to save.',
            disabled: true
          });
        } else {
          alert(err.message);
          setSaving({
            message: 'Err... Content unable to be saved.',
            disabled: false
          });
        }
      }
    }
  );

  const generateCertificateMutation = useMutation({
    mutationFn: async (params) => {
      return await postDataAPI('/userWorkbookItems/generateCert', params);
    },
    onSuccess: () => {
      setCertLoading(false);
      setCertification(true);
    },
    onError: (err) => {
      setCertLoading(false);
      if (!handleExpiredCreditError(err)) {
        alert(err.message);
      }
    }
  });

  const handlePrevClick = () => {
    setScore(false);
    if (activeId === 'outro') {
      if (progress.value === progress.maximumValue) {
        setSelectedSection('certificate');
        setShowSection('certificate');
        setActiveId('certificate');
        return;
      }

      const lastSection = data.sections.filter((item) => item).reverse()[0];
      const prevChild = lastSection.items.slice(-1).pop();

      setSelectedSection(prevChild);
      setActiveId(prevChild?._id);
      setShowSection(prevChild?.item_id.sectionid);
      return;
    }

    if (selectedSection === 'certificate') {
      const lastSection = data.sections.filter((item) => item.completed).reverse()[0];
      const prevChild = lastSection.items.slice(-1).pop();

      setSelectedSection(prevChild);
      setActiveId(prevChild?._id);
      setShowSection(prevChild?.item_id.sectionid);
      return;
    }

    const currentChild = data.sections.flatMap((section) => section.items).find((child) => child._id === activeId);
    const currentIndex = data.sections.flatMap((section) => section.items).indexOf(currentChild);
    const prevChild = data.sections.flatMap((section) => section.items)[currentIndex - 1];
    if (!prevChild) {
      setSelectedSection('intro');
      setActiveId('intro');
      setShowSection('intro');
      return;
    }
    setActiveId(prevChild?._id);
    setShowSection(prevChild?.item_id.sectionid);
    setSelectedSection(prevChild);
  };

  const progress = useMemo(() => {
    const sections = data && data.sections ? data.sections.flatMap((section) => section.items) : [];
    const filteredSections = sections.filter((item) => item?.item_id.completed);
    const value = filteredSections.length;
    const maximumValue = sections.length;
    return {
      value,
      maximumValue
    };
  }, [data]);

  const formik = useFormik({
    initialValues: {},
    enableReinitialize: true
  });

  const handleNext = async () => {
    setScore(false);
    const currentChild = data.sections.flatMap((section) => section.items).find((child) => child._id === activeId);
    const currentIndex = data.sections.flatMap((section) => section.items).indexOf(currentChild);
    const nextChild = data.sections.flatMap((section) => section.items)[currentIndex + 1];
    if (selectedSection === 'certificate') {
      setSelectedSection('outro');
      setShowSection('outro');
      setActiveId('outro');
      return;
    }

    if (!nextChild) {
      if (progress.value === progress.maximumValue) {
        setSelectedSection('certificate');
        setShowSection('certificate');
        setActiveId('certificate');
        return;
      }

      setSelectedSection('outro');
      setShowSection('outro');
      setActiveId('outro');
      return;
    }

    setActiveId(nextChild?._id);
    setShowSection(nextChild?.item_id.sectionid);
    setSelectedSection(nextChild);
  };

  const handleSubmitWithNext = async () => {
    if (showSection === 'intro' || selectedSection === 'certificate') {
      handleNext();
      return;
    } else if (selectedSection === 'outro') {
      setSelectedSection('certificate');
      return;
    }
    const fields = formik.values;
    const activeItem = selectedSection.item_id;

    const questions = [];
    let scores = 0;
    const newUnansweredQuestions = [];

    if (!!activeItem && activeItem.type === 'Assessment') {
      activeItem.questions.forEach((question, i) => {
        const name = question.type + i;
        const value = fields[name];

        if (!value) {
          newUnansweredQuestions.push(name);
        }

        if (question.type === 'checkbox') {
          let checkboxScore = 0;
          question.options.forEach((option) => {
            if (value?.includes(option.title)) {
              checkboxScore += option.points;
            }
          });
          if (checkboxScore > 0) {
            scores += checkboxScore;
          } else {
            scores += 0;
          }
        } else if (question.type === 'radio') {
          const option = question.options?.find((o) => o.title === value);
          if (option) {
            scores += option.points;
          }
        } else if (value) {
          scores += question.points;
        }

        if (fields[name] !== undefined) {
          questions.push({
            question_id: question._id,
            answers: [
              {
                content: fields[name]
              }
            ]
          });
        }
      });

      if (scores < activeItem.required_score) {
        setErrorQuestions(newUnansweredQuestions);
        setScore(true);
        return;
      }
    }

    const params = {
      item_id: activeItem?._id,
      user_id: currentUser._id,
      credit_id: id,
      workbookid: data?._id,
      sectionid: activeItem?.sectionid,
      total: scores,
      questions
    };

    if (Object.keys(activeItem?.userWorkbookItem).length > 0) {
      await userworkbookItemUpdateMutation.mutate({
        id: activeItem?.userWorkbookItem?._id,
        param: params
      });
    }
    await userWorkbookItemMutation.mutate(params);

    handleNext();
    formik.resetForm();
    setErrorQuestions([]);
  };

  const handleSubmitWithoutNext = async () => {
    const fields = formik.values;
    const activeItem = selectedSection.item_id;
    if (activeItem === undefined || activeItem === null) return;

    const questions = [];
    let scores = 0;

    if (!!activeItem && activeItem.type === 'Assessment' && formik.values) {
      activeItem.questions.forEach((question, i) => {
        const name = question.type + i;
        const value = fields[name];

        if (question.type === 'checkbox') {
          let checkboxScore = 0;
          question.options.forEach((option) => {
            if (value?.includes(option.title)) {
              checkboxScore += option.points;
            }
          });
          if (checkboxScore > 0) {
            scores += checkboxScore;
          } else {
            scores += 0;
          }
        } else if (question.type === 'radio') {
          const option = question.options?.find((o) => o.title === value);
          if (option) {
            scores += option.points;
          }
        } else if (value) {
          scores += question.points;
        }

        if (fields[name] !== undefined) {
          questions.push({
            question_id: question._id,
            answers: [
              {
                content: fields[name]
              }
            ]
          });
        }
      });
    }

    const params = {
      item_id: activeItem._id,
      user_id: currentUser._id,
      credit_id: id,
      workbookid: data?._id,
      sectionid: activeItem.sectionid,
      total: scores,
      questions
    };

    if (Object.keys(activeItem?.userWorkbookItem).length > 0) {
      await userworkbookItemUpdateMutation.mutate({
        id: activeItem?.userWorkbookItem?._id,
        param: params
      });
    } else {
      await userWorkbookItemMutation.mutate(params);
    }
  };

  useEffect(() => {
    let isActive = true;

    if (!data) return;
    if (selectedSection === undefined || selectedSection === null) return;
    if (isActive) {
      if (selectedSection === 'certificate' && progress.value === progress.maximumValue) {
        if (!certification) {
          setCertLoading(true);
          generateCertificateMutation.mutate({
            user: {
              name: `${currentUser.firstName} ${currentUser.lastName}`,
              _id: currentUser._id
            },
            workbook: {
              title: data.title,
              _id: data._id,
              type: data.type
            },
            page: progress.value,
            credit_id: id
          });
        }
      }
    }
    return () => {
      isActive = false;
    };
  }, [selectedSection, progress, id]);

  useEffect(() => {
    scrollWorkbookContentToTop();
  }, [selectedSection]);

  
  if (!data) {
    return <Loading />;
  }

  if (!isLoading && error) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-6 rounded-xl bg-white p-12 shadow-lg">
          <h1 className="text-4xl font-bold text-gray-900">Workbook Not Found</h1>
          <Button link="/library">
            <ArrowBack />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Check if credit has expired - block access if expired
  if (data) {
    const creditData = data.expiresAt !== undefined ? data : data.credit;
    if (creditData && isCreditExpired(creditData)) {
      return (
        <div className="flex h-screen w-screen items-center justify-center bg-gray-50">
          <div className="flex flex-col items-center gap-6 rounded-xl bg-white p-12 shadow-lg">
            <h1 className="text-4xl font-bold text-gray-900">Credit Expired</h1>
            <p className="text-center text-lg text-gray-600">
              This credit expired on {formatExpirationDate(creditData)}.<br />
              Progress is no longer accessible.
            </p>
            <Button link="/library">
              <ArrowBack />
              Go Back to Library
            </Button>
          </div>
        </div>
      );
    }
  }

  // Helper: Check credit expiration status
  // Works with both V1 (data.credit) and V2 (data.expiresAt directly) workbooks
  const checkCreditExpiration = () => {
    if (!data) {
      return null;
    }

    // Use data directly if it has expiresAt, otherwise use data.credit
    const creditData = data.expiresAt !== undefined ? data : data.credit;

    if (!creditData) {
      return null;
    }

    const expired = isCreditExpired(creditData);
    const expiringSoon = isCreditExpiringSoon(creditData, 15); 
    const daysLeft = getDaysUntilExpiration(creditData);
    const formattedDate = formatExpirationDate(creditData);
    

    return {
      expired,
      expiringSoon,
      daysLeft,
      formattedDate
    };
  };
  const steps = [
    {
      title: 'Welcome to the new Digital Resource Experience!',
      description: 'Follow this quick tour to help you get started.'
    },
    {
      selector: '.joyride-progressbar',
      description: 'Progress for your resource will be shown here. '
    },
    {
      selector: '.joyride-tooldraweropen',
      description: 'Click here to open the tool drawer.',
      nextOnTargetClick: true,
      disableNext: true
    },
    {
      selector: '.joyride-notes',
      description: 'The Tool Drawer is where you add and retrieve notes related to the resource.',
      movingTarget: true,
      updateInterval: 50
    },
    {
      selector: '.joyride-tooldrawerclose',
      description: 'Click here to close the tool drawer.',
      nextOnTargetClick: true,
      disableNext: true,
      disableAutoScroll: true
    },
    {
      selector: '.joyride-next-prev',
      description: 'Use the left and right arrows to move forward and backward in the resource.'
    },
    {
      selector: '.joyride-nav',
      description: 'Or click here to quickly navigate the workbook.',
      movingTarget: true,
      nextOnTargetClick: true,
      updateInterval: 50
    },
    {
      selector: '.joyride-nav-open',
      description: 'You can jump to any lesson or section by selecting it from here.',
      movingTarget: true,
      updateInterval: 50
    },
    {
      selector: '.joyride-save-hint',
      description: "We'll automatically save your progress as you type or navigate."
    },
    {
      selector: '.joyride-exit',
      description: 'And if you need to get back to the dashboard you can click here.'
    },
    {
      title: "You're ready to get started!",
      description: 'Thank you'
    }
  ];

  const walktourFooter = ({ close, next, prev, stepIndex, allSteps }) => {
    return (
      <div className="flex w-full items-center justify-center gap-5 pt-10">
        <button
          onClick={() => {
            close();
            putDataAPI(`/user/takenWorkbookTour/${currentUser._id}`);
          }}
          className="ring-pdred text-pdred mr-10 rounded-lg p-3 ring-1 hover:cursor-pointer"
        >
          Close
        </button>
        <button onClick={prev} className="ring-pddarkblue rounded-lg p-3 ring-1 hover:cursor-pointer">
          Previous
        </button>
        <button
          onClick={() => {
            if (stepIndex === allSteps.length - 1) {
              close();
              putDataAPI(`/user/takenWorkbookTour/${currentUser._id}`);
            }
            next();
          }}
          className="bg-pddarkblue rounded-lg p-3 text-white hover:cursor-pointer"
        >
          {stepIndex === allSteps.length - 1 ? 'Finish' : 'Next'}
        </button>
      </div>
    );
  };

  const walktourTooltip = ({ allSteps, close, next, prev, stepContent, stepIndex }) => {
    switch (stepIndex) {
      case 3:
        setShowToolDrawer(true);
        break;
      case 5:
        setShowToolDrawer(false);
        break;
      case 7:
        setExpandNav(true);
        break;
      case 8:
        toast.promise(
          new Promise((resolve) => setTimeout(resolve, 3000)),
          {
            loading: 'Saving...',
            success: 'Saved'
          },
          {
            id: 'joyride'
          }
        );
        setExpandNav(false);
        break;
    }
    return (
      <div
        className={`flex w-[95dvw] flex-col items-center justify-center gap-5 rounded-xl bg-white p-10 pt-10 shadow-md ${stepIndex === 0 ? 'lg:w-[40dvw]' : 'lg:w-[30dvw]'}`}
      >
        <h2>{stepContent.title}</h2>
        {stepContent.description}
        {stepContent.customFooterRenderer({ close, next, prev, stepIndex, allSteps })}
      </div>
    );
  };

  return (
    <>
      {!currentUser?.takenWorkbookTour && (
        <Walktour
          steps={steps}
          customFooterRenderer={walktourFooter}
          customTooltipRenderer={walktourTooltip}
          disableAutoScroll={true}
        />
      )}
      <div className="bg-pdteal/15 no-scrollbar relative h-dvh overflow-hidden">
        <div className="joyride-end sticky top-0 z-50 flex flex-col bg-white">
          <div className="sticky top-0 flex justify-between p-3">
            <div className="grid h-[8dvh] w-full grid-cols-[2fr_5fr_1fr_3fr] grid-rows-[5dvh_1dvh] items-center gap-3 gap-y-5 lg:h-[6dvh] lg:grid-rows-1 lg:gap-0">
              <a href="/" className="row-span-all hidden lg:block">
                <img src={logo} alt="Logo" className="w-70" />
              </a>
              <div className="h-[6dvh] w-[6dvh] self-center rounded-2xl border-2 border-gray-200 p-1 shadow-sm lg:col-start-2 lg:row-start-1">
                <img
                  src={data?.file_cover ? `${API_URL}/${data.file_cover}` : workbookImageTemp}
                  className="h-full rounded-2xl"
                  alt="Workbook Cover Image"
                ></img>
              </div>
              <div className="col-span-full col-start-1 row-start-2 mt-1 h-min lg:col-span-1 lg:col-start-2 lg:row-start-1 lg:-mt-[3dvh] lg:ml-[8dvh]">
                <ProgressBar value={progress.value} maximumValue={progress.maximumValue} percentageBefore={true} />
              </div>
              <div className="col-start-2 col-end-3 flex flex-col gap-2 lg:-mt-[4dvh] lg:ml-[8dvh]">
                <span className="font-title text-4xl leading-10 font-normal text-gray-900 lg:text-5xl">
                  {data?.title}
                </span>
                {(data?.expiresAt || data?.credit) && (
                  <div className="flex items-center gap-2">
                    <CreditExpirationBadge
                      credit={data.expiresAt !== undefined ? data : data.credit}
                      showDate={true}
                      compact={false}
                    />
                  </div>
                )}
              </div>
              <a
                href="/"
                className="text-pddarkblue joyride-exit col-span-2 col-start-3 flex items-center justify-center gap-2 justify-self-end rounded-lg p-3 lg:col-start-4 lg:row-start-1 lg:justify-self-end"
              >
                <span className="text-xl whitespace-nowrap lg:text-2xl">{t('Save & Exit')}</span>
                <XCircleIcon className="size-10" />
              </a>
            </div>
          </div>
        </div>
        <div className="bg-pddarkblue/1 relative flex h-[100dvh] overflow-hidden rounded-lg lg:mx-5">
          {!currentUser?.takenWorkbookTour && (
            <>
              <div className="joyride-save-hint pointer-events-none absolute bottom-20 left-1/2 z-40 h-50 w-180 -translate-x-1/2 bg-transparent"></div>
              <div className="joyride-next-prev pointer-events-none absolute bottom-28 left-1/2 z-40 -mt-15 h-20 w-full -translate-1/2 bg-transparent lg:top-1/2"></div>
            </>
          )}
          <div
            className="relative mx-[2%] w-[96%] transition-all duration-400 lg:left-1/2 lg:w-2/3 data-[full=false]:lg:left-0"
            data-full={!showToolDrawer}
          >
            <Swipeable onSwipeLeft={handleSubmitWithNext} onSwipeRight={handlePrevClick}>
              <FormikProvider value={formik}>
                <form>
                  <div
                    className="fixed top-[6.5dvh] left-[2dvw] flex h-[90dvh] w-[96dvw] items-center justify-between transition-all duration-300 md:top-[9.1dvh] lg:w-[68dvw] data-[full=true]:lg:left-[16dvw]"
                    data-full={!showToolDrawer}
                  >
                    <Button
                      variant="primary"
                      onClick={handlePrevClick}
                      disabled={saving.disabled}
                      className="group absolute -bottom-5 flex items-center lg:static lg:-ml-15 lg:flex-col lg:items-end"
                    >
                      <ChevronDoubleLeftIcon className="size-10" />
                      <span
                        className="text-pddarkblue/80 text-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100 data-[show=true]:opacity-100"
                        data-show={showNextPrevHint}
                      >
                        Previous
                      </span>
                    </Button>
                    <div
                      className="no-scrollbar h-9/10 w-full overflow-x-hidden overflow-y-scroll rounded-xl bg-white px-10 pb-10 shadow-md lg:h-full"
                      id="workbookContent"
                      ref={lessonContentRef}
                      data-full={!showToolDrawer}
                    >
                      <div className="sticky top-0 z-50 flex w-full flex-col justify-between bg-white">
                        <div className="joyride-nav -ml-10 min-h-max w-[96dvw] bg-white lg:w-[90%]">
                          <WorkbookNav
                            sections={data?.sections}
                            currentSection={selectedSection}
                            expandNav={expandNav}
                            onClick={(data) => {
                              handleSelectedSectionChange(data);
                            }}
                          ></WorkbookNav>
                        </div>
                        <div className="-ml-10 h-[1px] w-screen bg-gray-200"></div>
                      </div>
                      {selectedSection === 'intro' && <RenderString content={data?.intro} />}
                      {selectedSection === 'outro' && <RenderString content={data?.outro} />}
                      {selectedSection === 'certificate' ? (
                        certLoading ? (
                          <Loading />
                        ) : (
                          certification && <Certification path={certification} user={currentUser} workbook={data} />
                        )
                      ) : (
                        <div className="min-h-max">
                          <WorkbookItem
                            description={selectedSection?.item_id?.description}
                            type={selectedSection?.item_id?.type}
                            content={selectedSection?.item_id?.content}
                            questions={selectedSection?.item_id?.questions}
                            formik={formik}
                            userWorkbookItem={selectedSection?.item_id?.userWorkbookItem}
                            handleSubmitWithoutNext={handleSubmitWithoutNext}
                            errorQuestions={errorQuestions}
                            setSaving={setSaving}
                          />
                        </div>
                      )}
                    </div>
                    <Button
                      variant="primary"
                      onClick={handleSubmitWithNext}
                      disabled={saving.disabled}
                      className="group absolute right-0 -bottom-5 flex flex-row-reverse items-center lg:static lg:-mr-3 lg:flex-col lg:items-start"
                    >
                      <ChevronDoubleRightIcon className="size-10" />
                      <span
                        className="text-pddarkblue/80 text-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100 data-[show=true]:opacity-100"
                        data-show={showNextPrevHint}
                      >
                        Next
                      </span>
                    </Button>
                  </div>
                </form>
              </FormikProvider>
            </Swipeable>
          </div>
          <ToolDrawer title="Notes" ref={notebookDrawer} open={showToolDrawer} setOpen={setShowToolDrawer}>
            <Formik onSubmit={() => handleSubmitEntry(selectedEntry)} initialValues={{ title: '', content: '' }}>
              <Form className="joyride-notes flex flex-col gap-5 pt-5">
                <label htmlFor="title">
                  <div className="flex h-10 items-center justify-between">
                    <span className="text-2xl/4 leading-7 font-bold text-gray-600">Title</span>
                  </div>
                  <Field
                    id="title"
                    name="title"
                    placeholder={t('Optional')}
                    value={noteTitle}
                    className="focus:ring-pdteal mt-3 h-1/2 w-full rounded-md border border-gray-400 p-3 focus:ring focus:outline-none"
                    onChange={(e) => {
                      setNoteTitle(e.target.value);
                    }}
                  />
                </label>
                <label htmlFor="content">
                  <span className="text-2xl leading-7 font-bold text-gray-600">Content</span>
                  <Field
                    as="textarea"
                    rows="15"
                    id="content"
                    value={initContent}
                    name="content"
                    onChange={(e) => {
                      setInitContent(e.target.value);
                    }}
                    className="focus:ring-pdteal mt-3 h-1/2 w-full rounded-md border border-gray-400 p-3 font-normal focus:ring focus:outline-none"
                  />
                </label>
              </Form>
            </Formik>
            <PreviousNoteGroup
              user={currentUser}
              creditId={id}
              lastSubmit={lastNoteSubmit}
              newNoteClick={handleClearNote}
              setSelectedEntry={setSelectedEntry}
            ></PreviousNoteGroup>
          </ToolDrawer>
        </div>
      </div>
    </>
  );
};

export default Book;
